import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface SelectedFeature {
  id: string;
  layerId: string;
  properties: Record<string, unknown>;
  geometry?: unknown;
}

export type ActiveTool =
  | 'select'
  | 'pan'
  | 'zoom'
  | 'measure-distance'
  | 'measure-area'
  | 'draw-point'
  | 'draw-line'
  | 'draw-polygon'
  | null;

interface UIState {
  selectedFeatures: SelectedFeature[];
  activeTool: ActiveTool;
  sidebarOpen: boolean;
  layerPanelOpen: boolean;
  propertiesPanelOpen: boolean;
  isDrawing: boolean;
  isMeasuring: boolean;
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    timestamp: number;
  }>;
  // Excellent UI for non-technical users
  isNonTechnicalMode: boolean; // Friendly guided mode vs full power user
  currentTaskWizard: string | null; // e.g. 'draw-boundary', 'upload-data', 'generate-report'
  /** When true, map clicks keep Identify results panel open (QGIS-style) */
  identifyMode: boolean;
}

const getInitialNonTechnicalMode = (): boolean => {
  try {
    const saved = localStorage.getItem('geovera_user_mode');
    if (saved === 'advanced') return false;
    if (saved === 'simple') return true;
  } catch {
    /* ignore */
  }
  return true; // Default to Simple Mode for new/first-time users
};

const initialState: UIState = {
  selectedFeatures: [],
  activeTool: null,
  sidebarOpen: true,
  layerPanelOpen: true,
  propertiesPanelOpen: false,
  isDrawing: false,
  isMeasuring: false,
  notifications: [],
  // Default to Simple Mode for non-technical user experience
  isNonTechnicalMode: getInitialNonTechnicalMode(),
  currentTaskWizard: null,
  identifyMode: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    selectFeature: (state, action: PayloadAction<SelectedFeature>) => {
      state.selectedFeatures = [action.payload];
      state.propertiesPanelOpen = true;
    },
    addToSelection: (state, action: PayloadAction<SelectedFeature>) => {
      state.selectedFeatures.push(action.payload);
      state.propertiesPanelOpen = true;
    },
    clearSelection: (state) => {
      state.selectedFeatures = [];
      state.propertiesPanelOpen = false;
    },
    setActiveTool: (state, action: PayloadAction<ActiveTool>) => {
      state.activeTool = action.payload;
      state.isDrawing = action.payload?.startsWith('draw-') ?? false;
      state.isMeasuring = action.payload?.startsWith('measure-') ?? false;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleLayerPanel: (state) => {
      state.layerPanelOpen = !state.layerPanelOpen;
    },
    setLayerPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.layerPanelOpen = action.payload;
    },
    togglePropertiesPanel: (state) => {
      state.propertiesPanelOpen = !state.propertiesPanelOpen;
    },
    setPropertiesPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.propertiesPanelOpen = action.payload;
    },
    addNotification: (
      state,
      action: PayloadAction<Omit<UIState['notifications'][0], 'id' | 'timestamp'>>
    ) => {
      state.notifications.push({
        ...action.payload,
        id: `notification-${Date.now()}-${Math.random()}`,
        timestamp: Date.now(),
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    // Non-technical user friendly UI controls
    setNonTechnicalMode: (state, action: PayloadAction<boolean>) => {
      const wasNonTech = state.isNonTechnicalMode;
      state.isNonTechnicalMode = action.payload;
      try {
        localStorage.setItem('geovera_user_mode', action.payload ? 'simple' : 'advanced');
      } catch {
        /* ignore */
      }
      // Robust reset: close wizards, clear tools when switching modes to prevent broken state
      if (wasNonTech !== action.payload) {
        state.currentTaskWizard = null;
        state.activeTool = null;
        state.isDrawing = false;
        state.isMeasuring = false;
        state.selectedFeatures = [];
        state.propertiesPanelOpen = false;
      }
    },
    startTaskWizard: (state, action: PayloadAction<string>) => {
      state.currentTaskWizard = action.payload;
    },
    endTaskWizard: (state) => {
      state.currentTaskWizard = null;
    },
    setIdentifyMode: (state, action: PayloadAction<boolean>) => {
      state.identifyMode = action.payload;
    },
  },
});

export const {
  selectFeature,
  addToSelection,
  clearSelection,
  setActiveTool,
  toggleSidebar,
  setSidebarOpen,
  toggleLayerPanel,
  setLayerPanelOpen,
  togglePropertiesPanel,
  setPropertiesPanelOpen,
  addNotification,
  removeNotification,
  clearNotifications,
  setNonTechnicalMode,
  startTaskWizard,
  endTaskWizard,
  setIdentifyMode,
} = uiSlice.actions;

export default uiSlice.reducer;
